import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// Settings → Recall
//
// Edits clinic_settings.extras.recall.* — the knobs the recall state
// machine reads (log_recall_attempt + the three pg jobs).
// Permission-gated on recall.settings.manage; the RPC enforces it and
// we render a denied state if it throws.
// =====================================================================

const DEFAULTS = {
  cycle_count:                3,
  cooldown_days:              7,
  initial_wait_days:          2,
  cold_pile_resurface_months: 6,
  email_delay_minutes:        60,
  email_auto_send_enabled:    true,
  sort_mode:                  "conversion",
};

export default function SettingsRecallCenter() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [s, setS]             = useState(DEFAULTS);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("get_clinic_settings");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load recall settings."); return; }
    const remote = data?.extras?.recall ?? {};
    setS({ ...DEFAULTS, ...remote });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true); setError(null);
    const { error: e } = await supabase.rpc("set_recall_settings", { p_payload: s });
    setSaving(false);
    if (e) { setError(e.message ?? "Save failed."); return; }
    toast.success("Recall settings saved.");
  }

  function setKey(k, v) { setS((prev) => ({ ...prev, [k]: v })); }

  if (error && /permission denied/i.test(error)) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="recall-settings-denied">
        <Loader2 className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">You need <code className="text-xs bg-slate-100 px-1 rounded">recall.settings.manage</code>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-recall">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Recall</h1>
        <p className="text-xs text-slate-500">How patients move through the recall pipeline. These values drive the auto-progression jobs (cooldown promotion, cold-pile resurface) and the follow-up email cadence.</p>
      </div>

      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : (
        <div className="space-y-4">
          <Section title="Pipeline cadence" desc="When patients enter the queue and how the no-answer cycle progresses.">
            <NumberRow
              label="Initial wait after diagnosis"
              desc="How long to wait after a treatment is diagnosed before the patient appears in the recall queue. Set to 0 to surface immediately."
              suffix="day(s)"
              min={0} max={30}
              val={s.initial_wait_days}
              onChange={(n) => setKey("initial_wait_days", n)}
              testId="recall-set-initial-wait"
            />
            <NumberRow
              label="Cycle cap before Cold Pile"
              desc="After this many no-answer attempts, the patient drops to Cold Pile. Higher = more outreach before giving up."
              suffix="cycle(s)"
              min={1} max={5}
              val={s.cycle_count}
              onChange={(n) => setKey("cycle_count", n)}
              testId="recall-set-cycle-count"
            />
            <NumberRow
              label="Cooldown between attempts"
              desc="Days between cycles after a no-answer before the row reopens to staff."
              suffix="day(s)"
              min={1} max={90}
              val={s.cooldown_days}
              onChange={(n) => setKey("cooldown_days", n)}
              testId="recall-set-cooldown"
            />
            <NumberRow
              label="Cold Pile resurface"
              desc="Months a patient sits in Cold Pile before auto-resurfacing back to Active with a 'Returning' badge."
              suffix="month(s)"
              min={1} max={24}
              val={s.cold_pile_resurface_months}
              onChange={(n) => setKey("cold_pile_resurface_months", n)}
              testId="recall-set-cold-resurface"
            />
          </Section>

          <Section title="Follow-up emails" desc="Automated email sent after a no-answer call. Wording is set in Settings → Email Templates.">
            <ToggleRow
              label="Auto-send follow-up emails"
              desc="When off, the queue still tracks no-answer attempts but no email is sent automatically. Staff can still send manually."
              checked={!!s.email_auto_send_enabled}
              onChange={(v) => setKey("email_auto_send_enabled", v)}
              testId="recall-set-email-enabled"
            />
            <NumberRow
              label="Send delay after no-answer"
              desc="How long to wait after a 'no-answer' call outcome before the follow-up email goes out."
              suffix="minute(s)"
              min={0} max={1440}
              val={s.email_delay_minutes}
              onChange={(n) => setKey("email_delay_minutes", n)}
              disabled={!s.email_auto_send_enabled}
              testId="recall-set-email-delay"
            />
          </Section>

          <Section title="Queue display" desc="How the Recall Center sorts rows by default.">
            <RadioRow
              label="Default sort"
              desc="Switch any time from the queue page itself."
              options={[
                { value: "conversion", label: "Conversion First", desc: "Patients most likely to book at the top." },
                { value: "revenue",    label: "Revenue First",    desc: "Highest-value patients at the top." },
              ]}
              value={s.sort_mode}
              onChange={(v) => setKey("sort_mode", v)}
              testId="recall-set-sort-mode"
            />
          </Section>

        </div>
      )}

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={saving || loading}
          data-testid="recall-settings-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm">
      <div className="px-5 py-3 border-b border-slate-100">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function NumberRow({ label, desc, suffix, min, max, val, onChange, disabled, testId }) {
  return (
    <div className={"flex items-start gap-4 px-5 py-3 " + (disabled ? "opacity-50" : "")}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <input
          type="number" min={min} max={max} disabled={disabled}
          data-testid={testId}
          value={val ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.max(min, Math.min(max, Number(e.target.value))))}
          className="w-20 px-2 py-1 text-sm rounded border border-slate-200 text-slate-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-100 outline-none disabled:bg-slate-50"
        />
        <span className="text-xs text-slate-500 whitespace-nowrap">{suffix}</span>
      </div>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-4 px-5 py-3 cursor-pointer">
      <input
        type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
    </label>
  );
}

function RadioRow({ label, desc, options, value, onChange, testId }) {
  return (
    <div className="px-5 py-3">
      <p className="text-sm font-medium text-slate-800">{label}</p>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">{desc}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((o) => (
          <label key={o.value}
            className={"flex items-start gap-3 px-3 py-2 rounded-lg border cursor-pointer " +
              (value === o.value ? "border-teal-500 bg-teal-50/50" : "border-slate-200 hover:bg-slate-50")}>
            <input
              type="radio" value={o.value}
              checked={value === o.value}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`${testId}-${o.value}`}
              className="mt-1 w-4 h-4 text-teal-600 focus:ring-teal-500"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">{o.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{o.desc}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
